import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Inbox, ListChecks, BarChart2, MessageCircle, Send, Loader2, Check,
  AlertCircle, Clock, X, Users
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Training Team workspace — 3 tabs + AI assistant chat.
 * Rendered inside an expandable section below the main TrainingTab library.
 */
const TrainingTeam = ({ authAxios, isAdmin }) => {
  const [tab, setTab] = useState("inbox");
  const [requests, setRequests] = useState([]);
  const [assignments, setAssignments] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showNewReq, setShowNewReq] = useState(false);

  const fetchTab = async (which) => {
    setLoading(true);
    try {
      if (which === "inbox") {
        const r = await authAxios.get(`${API_URL}/api/training/requests`);
        setRequests(r.data.items || []);
      } else if (which === "mine") {
        const r = await authAxios.get(`${API_URL}/api/training/assignments/me`);
        setAssignments(r.data);
      } else if (which === "progress") {
        const r = await authAxios.get(`${API_URL}/api/training/progress`);
        setProgress(r.data.rows || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchTab(tab); }, [tab]); // eslint-disable-line

  const resolve = async (reqId) => {
    const note = prompt("Resolution notes:");
    if (!note) return;
    await authAxios.put(`${API_URL}/api/training/requests/${reqId}/resolve`, { response: note });
    fetchTab("inbox");
  };
  const assignToMe = async (reqId) => {
    await authAxios.put(`${API_URL}/api/training/requests/${reqId}/assign`, {});
    fetchTab("inbox");
  };
  const completeAssignment = async (assignmentId) => {
    await authAxios.post(`${API_URL}/api/training/assignments/${assignmentId}/complete`);
    fetchTab("mine");
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="training-team">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#DC283C] text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18}/>
          <span className="font-bold text-sm">Training Team Workspace</span>
        </div>
        <button
          onClick={() => setShowAssistant(s => !s)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-semibold"
          data-testid="toggle-assistant-btn"
        >
          <MessageCircle size={12}/> Ask DoozyBot
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {[
          { id: "inbox", label: "Request Inbox", icon: Inbox },
          { id: "mine", label: "My Assignments", icon: ListChecks },
          { id: "progress", label: "Team Progress", icon: BarChart2, adminOnly: true },
        ].filter(t => !t.adminOnly || isAdmin).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition ${
              tab === t.id ? "text-[#DC283C] border-b-2 border-[#DC283C]" : "text-gray-400 hover:text-gray-600"
            }`}
            data-testid={`team-tab-${t.id}`}
          >
            <t.icon size={12}/> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4 min-h-[180px]">
        {loading && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#DC283C]" size={20}/></div>}

        {!loading && tab === "inbox" && (
          <div className="space-y-2">
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs text-gray-500">{requests.length} request(s) for your departments</p>
              <button onClick={() => setShowNewReq(true)} className="text-xs text-[#DC283C] font-semibold" data-testid="new-request-btn">+ New Request</button>
            </div>
            {requests.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-6">No training requests yet 🎊</p>
            ) : requests.map(r => (
              <div key={r.request_id} className="border border-gray-100 rounded-xl p-3 hover:border-[#DC283C]/30 transition" data-testid={`req-${r.request_id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1E1B4B]">{r.topic}</p>
                    {r.description && <p className="text-[11px] text-gray-500 mt-0.5">{r.description}</p>}
                    <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                      <span className="px-1.5 py-0.5 rounded bg-[#FFF0F6] text-[#DC283C] font-semibold">{r.department}</span>
                      <span className="text-gray-400">by {r.requester_username}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <StatusPill status={r.status}/>
                  </div>
                </div>
                {r.status !== "resolved" && (
                  <div className="flex gap-2 mt-2">
                    {r.status === "open" && (
                      <button onClick={() => assignToMe(r.request_id)} className="text-[10px] px-2 py-1 rounded border border-gray-200 hover:border-[#DC283C] hover:text-[#DC283C]" data-testid={`assign-${r.request_id}`}>
                        Assign to me
                      </button>
                    )}
                    <button onClick={() => resolve(r.request_id)} className="text-[10px] px-2 py-1 rounded bg-[#DC283C] text-white" data-testid={`resolve-${r.request_id}`}>
                      Resolve
                    </button>
                  </div>
                )}
                {r.status === "resolved" && r.response && (
                  <p className="text-[11px] text-emerald-600 mt-2 italic">✅ {r.response}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "mine" && assignments && (
          <div>
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500">My learning progress</p>
                <p className="text-xs font-bold text-[#1E1B4B]">{assignments.completed}/{assignments.total} · {assignments.completion_pct}%</p>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#DC283C] to-orange-500 transition-all" style={{ width: `${assignments.completion_pct}%` }}/>
              </div>
            </div>
            {assignments.items.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-6">No assignments yet — you're all caught up 🎉</p>
            ) : assignments.items.map(a => (
              <div key={a.assignment_id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-3 mb-2" data-testid={`assign-${a.assignment_id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.status === "completed" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                  {a.status === "completed" ? <Check size={14}/> : <Clock size={14}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1E1B4B] truncate">{a.item_title}</p>
                  <p className="text-[10px] text-gray-400">{a.department}</p>
                </div>
                {a.status !== "completed" && (
                  <button onClick={() => completeAssignment(a.assignment_id)} className="text-[10px] px-2 py-1 rounded bg-[#DC283C] text-white" data-testid={`complete-${a.assignment_id}`}>
                    Mark Done
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "progress" && (
          progress.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-6">No team progress data yet.</p>
          ) : (
            <div className="space-y-2">
              {progress.map(p => (
                <div key={p.user_username} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm font-semibold text-[#1E1B4B] w-32 truncate">{p.user_username}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#DC283C] to-orange-500" style={{ width: `${p.completion_pct}%` }}/>
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-12 text-right">{p.completion_pct}%</span>
                  <span className="text-[10px] text-gray-400 w-14 text-right">{p.completed}/{p.total}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showAssistant && <AssistantModal authAxios={authAxios} onClose={() => setShowAssistant(false)}/>}
      {showNewReq && <NewRequestModal authAxios={authAxios} onClose={() => setShowNewReq(false)} onSaved={() => { setShowNewReq(false); fetchTab("inbox"); }}/>}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    open: { bg: "bg-amber-50", fg: "text-amber-700", label: "Open" },
    assigned: { bg: "bg-blue-50", fg: "text-blue-700", label: "Assigned" },
    resolved: { bg: "bg-green-50", fg: "text-green-700", label: "Resolved" },
  };
  const s = map[status] || map.open;
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${s.bg} ${s.fg}`}>{s.label}</span>;
};

const NewRequestModal = ({ authAxios, onClose, onSaved }) => {
  const [form, setForm] = useState({ topic: "", description: "", department: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await authAxios.post(`${API_URL}/api/training/requests`, form);
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-request-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#1E1B4B]">New Training Request</h3>
        <input required placeholder="Topic (short)" value={form.topic} onChange={e => setForm({...form, topic: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="req-topic"/>
        <textarea placeholder="Describe what training you need" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={3}/>
        <input placeholder="Department (optional, e.g. ops)" value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm"/>
        {err && <p className="text-xs text-red-500">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs rounded-lg border">Cancel</button>
          <button type="submit" className="px-4 py-2 text-xs rounded-lg bg-[#DC283C] text-white font-semibold" data-testid="req-submit">Submit</button>
        </div>
      </form>
    </div>
  );
};

const AssistantModal = ({ authAxios, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste! I'm DoozyBot, your training assistant. Ask me about any training content for your department!" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = React.useRef(`trn-ui-${Date.now()}`);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setMessages(m => [...m, { role: "user", content: userMsg }]);
    setInput(""); setSending(true);
    try {
      const { data } = await authAxios.post(`${API_URL}/api/training/assistant/chat`, {
        message: userMsg, session_id: sessionRef.current,
      });
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Sorry — having trouble right now." }]);
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="assistant-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="px-4 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#DC283C] text-white flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl">
          <div className="flex items-center gap-2">
            <MessageCircle size={18}/>
            <span className="font-bold text-sm">DoozyBot · Training Assistant</span>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-assistant"><X size={18}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
          {messages.map((m, i) => (
            <div key={m.id || `m-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                m.role === "user" ? "bg-[#DC283C] text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"
              }`}>{m.content}</div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="bg-gray-100 rounded-2xl px-3 py-2 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> thinking…</div></div>}
        </div>
        <div className="border-t p-3 flex gap-2">
          <input
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask about training, procedures…"
            className="flex-1 px-3 py-2 border rounded-xl text-sm outline-none focus:border-[#DC283C]"
            data-testid="assistant-input"
            disabled={sending}
          />
          <button onClick={send} disabled={sending || !input.trim()} className="px-3 py-2 rounded-xl bg-[#DC283C] text-white disabled:opacity-50" data-testid="assistant-send">
            <Send size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrainingTeam;
